import React from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Image from '../image';
import materialIcons from '../icon/constants/material-icons-constants';
import { Span } from '../box';
import Icon from '../icon';
import Text from '../text';
import { colors, spacing } from '../../style';

import DotsLoader from '../dots-loader';

export const variants = {
  action: css`
    &:active,
    &.active,
    &:focus:not([data-highlighted]) {
      background: ${props => props.theme.actionLight};
      * {
        color: ${props => props.theme.actionColor};
      }
    }
  `,
  gray400: css`
    &:active,
    &.active,
    &:focus:not([data-highlighted]) {
      background: ${colors.gray400};
    }
  `,
};

export interface MenuItemProps {
  text?: string | React.ReactNode;
  icon?: keyof typeof materialIcons;
  className?: string;
  children?: React.ReactNode;
  outlined?: boolean;
  componentClass?: React.ElementType | string;
  capitalizeFirstLetter?: boolean;
  whiteSpace?: 'pre-line' | 'break-spaces' | 'nowrap' | 'pre-wrap';
  isLoading?: boolean;
  image?: string;
  disabled?: boolean;
  marked?: boolean;
  active?: boolean;
  endIcon?: React.ReactNode;
  'data-name'?: string;
  'data-value'?: string;
  'data-bi'?: string;
  'data-testid'?: string;
  to?: string | (() => void);
  href?: string;
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  target?: '_blank';
  alt?: string;
  role?: 'menuitem' | 'option' | 'presentation';
  rel?: string;
  variant?: keyof typeof variants;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

export const MenuItemComponent = ({
  text,
  icon,
  className,
  children,
  outlined,
  componentClass: Component = 'div',
  capitalizeFirstLetter,
  whiteSpace,
  isLoading,
  image,
  disabled,
  marked,
  active,
  endIcon,
  'data-name': dataName,
  'data-value': dataValue,
  'data-bi': dataBi,
  'data-testid': dataTestId,
  to,
  href,
  onClick,
  onMouseEnter,
  onMouseLeave,
  target,
  alt,
  role = 'menuitem',
  ...rest
}: MenuItemProps) => {
  return (
    <Component
      {...rest}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      to={to}
      href={href}
      data-name={dataName}
      data-testid={dataTestId}
      data-value={dataValue}
      className={classNames('menu-item', className, { active }, { disabled }, { marked })}
      data-bi={dataBi}
      target={target}
      role={role}
    >
      {image && <Image alt={alt || 'menu item image'} src={image} width={15} />}
      {icon &&
        (isLoading ? (
          <DotsLoader size={'xxs'} variant={'gray400'} />
        ) : (
          <Icon size={'lg'} outlined={outlined} variant={'inherit'} name={icon} />
        ))}
      {text && (
        <Text
          marginLeft={image || icon ? 'xs' : 'none'}
          size={'md'}
          weight={300}
          marginBottom={'none'}
          variant={'inherit'}
          capitalizeFirstLetter={capitalizeFirstLetter}
          whiteSpace={whiteSpace}
        >
          {text}
        </Text>
      )}
      {children}

      <Span className={'end-icon'}>{endIcon}</Span>
    </Component>
  );
};

const MenuItem = styled(MenuItemComponent)<MenuItemProps>`
  border-radius: 4px;
  transition: all 250ms ease-out;
  cursor: pointer;
  padding: ${spacing.xs};
  display: flex;
  align-items: center;
  color: ${colors.gray900};

  i,
  span,
  .end-icon {
    line-height: 1.25rem;
  }

  &:hover,
  &.marked,
  &[data-highlighted] {
    background: ${colors.gray100};
  }

  ${props => variants[props.variant || 'action']}

  &.disabled {
    background: transparent;
    color: ${colors.gray400};
    cursor: not-allowed;

    &,
    &:hover,
    &.active,
    &.active {
      > * {
        background: transparent;
        color: ${colors.gray400};
      }
    }
  }

  .end-icon {
    margin-left: auto;
  }
`;

export type AccessibleMenuItemProps = MenuItemProps & {
  preventDefault?: boolean;
  stopPropagation?: boolean;
  role?: 'option';
};
export const AccessibleMenuItem = ({
  disabled,
  onClick,
  stopPropagation,
  preventDefault,
  ...rest
}: AccessibleMenuItemProps): React.JSX.Element => (
  <DropdownMenu.Item
    asChild
    disabled={disabled}
    // stop propagation on keyboard click as well
    onKeyDown={e => e.stopPropagation()}
    onClick={e => {
      if (stopPropagation) {
        e.stopPropagation();
      }

      if (preventDefault) {
        e.preventDefault();
      }

      if (onClick && !disabled) {
        onClick(e);
      }
    }}
  >
    <MenuItem disabled={disabled} {...rest} />
  </DropdownMenu.Item>
);

MenuItem.displayName = 'MenuItem';
export default MenuItem;
